import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SingleResponseModel } from '../../common/models/single-response.model';

export interface EnsureBookingMerchantResponse {
  merchantId: string;
  created: boolean;
}

export interface BookingMerchantStatusResponse {
  isActive: boolean;
  subscriptionId?: string;
  status?: string;
}

export interface BookingMerchantDeactivateResponse {
  deactivated: boolean;
  isActive: boolean;
  merchantId?: string;
}

export interface BookingSubscriptionCheckoutResponse {
  sessionId?: string;
  checkoutSessionId?: string;
  checkoutUrl?: string;
  trialEndDate?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BookingContextService {
  private readonly baseUrl = `${environment.apiUrl}/api/v2/booking/merchants`;
  private readonly subscriptionUrl = `${environment.apiUrl}/api/v2/subscription/booking`;
  private cachedMerchantId: string | null = null;

  constructor(private http: HttpClient) {}

  ensureBookingMerchant(): Observable<SingleResponseModel<EnsureBookingMerchantResponse>> {
    if (this.cachedMerchantId) {
      return of({
        isSuccess: true,
        value: { merchantId: this.cachedMerchantId, created: false }
      } as SingleResponseModel<EnsureBookingMerchantResponse>);
    }

    return this.http.post<SingleResponseModel<EnsureBookingMerchantResponse>>(
      `${this.baseUrl}/ensure`,
      {}
    ).pipe(
      tap(response => {
        if (response.isSuccess && response.value) {
          this.cachedMerchantId = response.value.merchantId;
        }
      }),
      catchError(error => {
        console.error('Error ensuring booking merchant:', error);
        throw error;
      })
    );
  }

  getCachedMerchantId(): string | null {
    return this.cachedMerchantId;
  }

  getBookingMerchantStatus(): Observable<SingleResponseModel<BookingMerchantStatusResponse>> {
    return this.http.get<SingleResponseModel<BookingMerchantStatusResponse>>(`${this.subscriptionUrl}/status`);
  }

  deactivateBookingMerchant(): Observable<SingleResponseModel<BookingMerchantDeactivateResponse>> {
    return this.http.post<SingleResponseModel<BookingMerchantDeactivateResponse>>(`${this.baseUrl}/deactivate`, {}).pipe(
      tap(response => {
        if (response && response.isSuccess && response.value && !response.value.isActive) {
          this.clearCache();
        }
      })
    );
  }

  clearCache(): void {
    this.cachedMerchantId = null;
  }

  activateBookingSubscriptionCheckout(): Observable<SingleResponseModel<BookingSubscriptionCheckoutResponse>> {
    return this.http.post<SingleResponseModel<BookingSubscriptionCheckoutResponse>>(
      `${this.subscriptionUrl}/checkout`,
      {}
    ).pipe(
      catchError(error => {
        console.error('Error activating booking subscription checkout:', error);
        throw error;
      })
    );
  }
}
